import React from "react";
import { candidateStatusOptions } from "../../../../../hooks/useRecruitmentCandidates";
import CandidateStatusBadge from "./CandidateStatusBadge";

// Custom timeline styles
const timelineStyles = `
  .timeline-container {
    position: relative;
  }
  
  .timeline-item {
    position: relative;
  }
  
  .timeline-content {
    background: #fff;
    border-radius: 8px;
    padding: 1rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    border: 1px solid #e9ecef;
  }
  
  .timeline-icon {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 8px;
    flex-shrink: 0;
    margin-top: 2px;
  }
  
  .timeline-title {
    font-size: 0.95rem;
    line-height: 1.3;
    color: #333;
  }
  
  .timeline-description {
    font-size: 0.9rem;
    line-height: 1.4;
    margin: 0.5rem 0;
  }
  
  .timeline-meta {
    font-size: 0.8rem;
    border-top: 1px solid #f8f9fa;
    padding-top: 0.5rem;
    margin-top: 0.75rem;
  }
  
  .timeline-divider {
    width: 2px;
    height: 1rem;
    background: #e9ecef;
    margin: 0 auto;
    position: relative;
    left: 11px;
  }
  
  .bg-secondary { background-color: #6c757d !important; }
  .bg-info { background-color: #17a2b8 !important; }
  .bg-primary { background-color: #007bff !important; }
  .bg-danger { background-color: #dc3545 !important; }
  .bg-warning { background-color: #ffc107 !important; }
  .bg-success { background-color: #28a745 !important; }
  .bg-dark { background-color: #343a40 !important; }
`;

const TIMELINE_COLOR_MAP = {
  REGISTERED: "secondary",
  SCREENING: "info",
  SELECTED: "primary",
  REJECTED: "danger",
  DOCUMENTS_SENT: "warning",
  OFFER_ACCEPTED: "success",
  APPOINTMENT: "dark",
};

const formatDateTime = (value) => {
  if (!value) return "";
  try {
    const date = new Date(value);
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    return value;
  }
};

const getStatusLabel = (statusValue) => {
  const statusOption = candidateStatusOptions.find(
    (option) => option.value === statusValue
  );
  return statusOption ? statusOption.label : statusValue;
};

const CandidateTimeline = ({ events = [] }) => {
  if (!events.length) {
    return (
      <div className="text-center text-muted py-4">
        <i className="fa fa-clock-o fa-2x mb-3"></i>
        <p className="mb-0">No activities recorded yet.</p>
      </div>
    );
  }

  return (
    <>
      <style>{timelineStyles}</style>
      <div className="timeline-container">
        {events.map((event, index) => {
          const status = event.new_status || event.status;
          const badgeColor =
            TIMELINE_COLOR_MAP[status?.toUpperCase?.()] || "secondary";

          return (
            <div
              key={event.id || `${status}-${event.changed_at}-${index}`}
              className="timeline-item"
            >
              <div className="timeline-content">
                <div className="d-flex align-items-start">
                  <div className={`timeline-icon bg-${badgeColor}`}>
                    <i className="fa fa-circle"></i>
                  </div>
                  <div className="timeline-details flex-grow-1 ms-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <h6 className="timeline-title mb-0">
                        <strong>
                          {getStatusLabel(event.new_status || event.status)
                            ?.replace(/_/g, " ")
                            ?.charAt(0)
                            .toUpperCase() +
                            getStatusLabel(event.new_status || event.status)
                              ?.replace(/_/g, " ")
                              ?.slice(1)
                              .toLowerCase() || "Update"}
                        </strong>
                      </h6>
                    </div>

                    {event.notes && (
                      <p
                        className="timeline-description mb-2 text-muted"
                        style={{
                          wordBreak: "break-word",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {event.notes}
                      </p>
                    )}

                    <div
                      className="timeline-meta d-flex justify-content-between align-items-center text-muted"
                      style={{ fontSize: "11px" }}
                    >
                      <span className="d-flex align-items-center">
                        <i className="fa fa-user me-1"></i>
                        {event.changed_by_name || "Candidate"}
                      </span>
                      <span className="d-flex align-items-center">
                        <i className="fa fa-clock-o me-1"></i>
                        {formatDateTime(event.changed_at || event.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {index < events.length - 1 && (
                <div className="timeline-divider"></div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default CandidateTimeline;
